import {COMPANY_KEYS} from "@/api/companyApi";
import BookmarkOutlineIcon from "@/assets/colorable/bookmark_outline.svg?react";
import InfoIcon from "@/assets/colorable/info.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import AnimatedAccordion from "@/components/Common/animated/AnimatedAccordion";
import Toggle from "@/components/Common/forms/Toggle";
import HighlightedHTMLTextArea from "@/components/Common/HighlightedHTMLTextArea";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import clsxm from "@/lib/clsxm";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const STAR_URLS = {
  full: `${window.location.origin}/referral/filled_star.svg`,
  half: `${window.location.origin}/referral/half_filled_star.svg`,
  empty: `${window.location.origin}/referral/empty_star.svg`,
};

const ReferralWebsiteBadge: FC<{link: string; slug?: string | null}> = ({link, slug}) => {
  const {t} = useTranslation("referral");
  const [shouldShowBadge, setShowBadge] = useState(true);
  const [shouldShowRating, setShowRating] = useState(true);
  const [shouldShowCta, setShowCta] = useState(true);
  const [isOpen, setOpen] = useState(false);

  const {data: ratingsData} = useGenericQuery<{average_rating: number; number_of_ratings: number}>({
    queryKey: COMPANY_KEYS.qCompanyPublicRatings(slug!),
    enabled: !!slug,
  });

  const html = `<!-- Influencer referral badge -->
<div id="refluenced-referral-badge"
  data-link="${link}"
  data-slug="${slug}" data-badge="${shouldShowBadge}" data-rating="${shouldShowRating}" data-cta="${shouldShowCta}">
</div>
<script src="${window.location.origin}/referral/referral-badge.js"></script>
  `
    .split("\n")
    .filter(line => line.trim() !== "")
    .join("\n");

  return (
    <div className="flex flex-col">
      <div
        className={clsxm(
          "bg-neutral-grey-lightest flex cursor-pointer items-center justify-between rounded-lg p-3 pr-5 transition-all",
          isOpen && "rounded-b-none",
        )}
        onClick={() => setOpen(value => !value)}>
        <div className="flex items-center gap-3.5">
          <div className="flex size-10 items-center justify-center rounded-md bg-white">
            <BookmarkOutlineIcon className="text-primary-dark-solid size-3.5 shrink-0" />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-neutral-black text-sm leading-2.5 font-semibold">{t("website_badge")}</span>
            <span className="text-neutral-slate-grey text-2xs leading-3">{t("website_badge_short_description")}</span>
          </div>
        </div>
        <RightChevronIcon
          className={clsxm("text-neutral-black size-3 transition-all", isOpen ? "-rotate-90" : "rotate-90")}
        />
      </div>
      <AnimatedAccordion condition={isOpen}>
        <div className="bg-neutral-grey-lightest border-t-neutral-grey-light-dark flex flex-col gap-5 border-t px-8 py-3">
          <div className="text-neutral-black flex flex-col gap-5 text-xs sm:flex-row">
            <div className="flex items-center gap-2.5">
              <Toggle checked={shouldShowBadge} onChange={setShowBadge} />
              <span>{t("badge")}</span>
            </div>
            <div className="flex items-center gap-2.5">
              <Toggle checked={shouldShowRating} onChange={setShowRating} />
              <span>{t("show_rating")}</span>
            </div>
            <div className="flex items-center gap-2.5">
              <Toggle checked={shouldShowCta} onChange={setShowCta} />
              <span>{t("show_apply_cta")}</span>
            </div>
          </div>
          {ratingsData?.number_of_ratings === 0 && shouldShowRating && (
            <div className="border-neutral-grey-light-dark flex min-h-11 items-center gap-2 rounded-md border bg-white px-5 py-3">
              <InfoIcon className="text-neutral-grey-dark size-3.5 shrink-0" />
              <span className="text-neutral-black text-sm">{t("no_ratings_yet")}</span>
            </div>
          )}
          <button
            className="m-0.5 flex w-fit flex-col items-center gap-2 self-center rounded-lg bg-white p-3 pt-2 shadow-sm"
            onClick={() => window.open(link, "_blank")}>
            <img
              className={clsxm(shouldShowBadge ? "h-16 w-19.5" : "size-6")}
              src={`${window.location.origin}/referral/${shouldShowBadge ? "top-rated-badge" : "logo-24x24"}.png`}
              alt="logo"
            />
            <div className="text-2xs text-center font-bold">refluenced</div>
            {shouldShowRating && !!ratingsData?.number_of_ratings && (
              <>
                <div className="flex items-center gap-1">
                  {Array.from({length: 5}).map((_, i) => {
                    const rating = ratingsData.average_rating;
                    const fullStars = Math.floor(rating);
                    const hasHalf = rating - fullStars >= 0.5;
                    const starType = i < fullStars ? "full" : i === fullStars && hasHalf ? "half" : "empty";
                    const starSrc =
                      starType === "full" ? STAR_URLS.full : starType === "half" ? STAR_URLS.half : STAR_URLS.empty;

                    return <img key={i} src={starSrc} alt={`${starType} star`} className="size-3" />;
                  })}
                </div>
                <div className="text-2xs text-center font-bold">
                  {ratingsData.average_rating} ({ratingsData.number_of_ratings})
                </div>
              </>
            )}
            {shouldShowCta && (
              // A div, not a button: the whole card is already the click target and nested buttons are invalid HTML.
              <div className="bg-primary-dark-solid text-2xs flex w-full items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 font-semibold text-white">
                <span>{t("apply_as_influencer")}</span>
                <RightArrowIcon className="size-2.5 shrink-0" />
              </div>
            )}
          </button>
          <div className="max-w-[calc(100vw-64px)]">
            <HighlightedHTMLTextArea code={html} />
          </div>
        </div>
      </AnimatedAccordion>
    </div>
  );
};

export default ReferralWebsiteBadge;
